import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Navigate, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ShoppingBag } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const LoginForm = () => {
  const { login, loading, error, isAuthenticated, clearError } = useAuth();
  const [formData, setFormData] = useState({
    username: '', // Peut être email ou téléphone
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Clear error uniquement au démontage du composant
  useEffect(() => {
    return () => clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rediriger si déjà connecté
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear global error when user starts typing
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.username.trim()) {
      errors.username = 'Email ou téléphone requis';
    }
    
    if (!formData.password) {
      errors.password = 'Mot de passe requis';
    } else if (formData.password.length < 6) {
      errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const result = await login({
      username: formData.username.trim(),
      password: formData.password
    });
    
    if (!result.success) {
      // Les erreurs sont gérées dans le contexte Auth
      console.error('Erreur de connexion:', result.error);
    }
  };

  const isEmailFormat = (value) => {
    return value.includes('@');
  };

  const formatPlaceholder = () => {
    return 'exemple@email.com ou +221 XX XXX XX XX';
  };

  return (
    <div className="smart-login-page">
      {/* Particules flottantes d'arrière-plan */}
      <div className="floating-elements">
        <div className="floating-element" style={{ top: '10%', left: '10%', animationDelay: '0s' }}>💼</div>
        <div className="floating-element" style={{ top: '20%', right: '15%', animationDelay: '2s' }}>🛍️</div>
        <div className="floating-element" style={{ top: '60%', left: '5%', animationDelay: '4s' }}>💳</div>
        <div className="floating-element" style={{ bottom: '20%', right: '10%', animationDelay: '6s' }}>📊</div>
        <div className="floating-element" style={{ bottom: '30%', left: '20%', animationDelay: '8s' }}>🏪</div>
      </div>

      <Container fluid className="min-vh-100 d-flex align-items-center justify-content-center position-relative">
        <Row className="w-100 justify-content-center">
          <Col xs={12} sm={10} md={8} lg={6} xl={5} xxl={4}>
            <Card className="smart-login-card shadow-2xl border-0">
              <Card.Body className="p-0">
                {/* Header avec gradient */}
             
                  <div className="login-header-section">
                    <div className="login-brand-container">
                      <div className="brand-icon-wrapper">
                        <div>
                          {/* Remplacer ShoppingBag par une image */}
                          <img 
                            src="smartPngBlanc.png" 
                            alt="SmartBoutik Logo" 
                            className="brand-logo-image"
                            width={150}
                            height={100}
                          />
                        </div>
                        <div className="brand-glow"></div>
                      </div>
                      {/* <h1 className="brand-title">SmartBoutik</h1> */}
                    </div>
                  </div>

                {/* Contenu principal */}
                <div className="login-content-section">
                  <div className="welcome-message">
                    <p>Connectez-vous pour accéder à votre tableau de bord</p>
                  </div>

                  {/* Alert d'erreur avec style amélioré */}
                  {error && (
                    <div className="custom-alert error-alert">
                      <div className="alert-icon">⚠️</div>
                      <div className="alert-content">
                        <strong>Erreur de connexion</strong>
                        <p>{error}</p>
                      </div>
                      <button className="alert-close" onClick={clearError}>×</button>
                    </div>
                  )}

                  {/* Formulaire avec design moderne */}
                  <Form onSubmit={handleSubmit} className="smart-form">
                    {/* Champ Username */}
                    <div className="form-group-modern">
                      <div className="input-wrapper">
                        <div className="input-icon">
                          <Mail size={20} />
                        </div>
                        <Form.Control
                          type="text"
                          name="username"
                          value={formData.username}
                          onChange={handleInputChange}
                          placeholder=" "
                          isInvalid={!!formErrors.username}
                          className="modern-input"
                          autoComplete="username"
                          autoFocus
                        />
                        <label className="floating-label">
                          Nom d'utilisateur
                        </label>
                      </div>
                      {formErrors.username && (
                        <div className="error-message">
                          <span className="error-icon">❌</span>
                          {formErrors.username}
                        </div>
                      )}
                      <div className="input-hint">
                        {formData.username && isEmailFormat(formData.username) 
                          ? '📧 Format email détecté' 
                          : formData.username 
                          ? '📱 Format téléphone détecté' 
                          : 'exemple@email.com ou +221 XX XXX XX XX'
                        }
                      </div>
                    </div>

                    {/* Champ Password */}
                    <div className="form-group-modern">
                      <div className="input-wrapper">
                        <div className="input-icon">
                          <Lock size={20} />
                        </div>
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder=" "
                          isInvalid={!!formErrors.password}
                          className="modern-input"
                          autoComplete="current-password"
                        />
                        <label className="floating-label">
                          Mot de passe
                        </label>
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {formErrors.password && (
                        <div className="error-message">
                          <span className="error-icon">❌</span>
                          {formErrors.password}
                        </div>
                      )}
                    </div>

                    {/* Bouton de connexion moderne */}
                    <Button
                      type="submit"
                      className="smart-login-btn"
                      disabled={loading}
                    >
                      <div className="btn-content">
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              className="btn-spinner"
                            />
                            <span>Connexion en cours...</span>
                          </>
                        ) : (
                          <>
                            <Lock size={18} />
                            <span>Se Connecter</span>
                            <div className="btn-arrow">→</div>
                          </>
                        )}
                      </div>
                      <div className="btn-glow"></div>
                    </Button>
                  </Form>

                  {/* Liens et footer */}
                  <div className="login-extras">
                    <Link to="/forgot-password" className="forgot-password-link">
                      <span className="link-icon">🔑</span>
                      Mot de passe oublié ?
                    </Link>
                  </div>

                  <div className="login-footer">
                    <div className="footer-divider"></div>
                    <p className="footer-text">
                      <a href='https://atlanticdigitalsolutions-sn.com/' style={{ textDecoration: "none" }}>
                      <span className="brand-name"> Atlantic digital solutions</span> © 2025
                      <br /> </a>
                      <small>Gestion de boutiques simplifiée et intelligente</small>
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <style>{`
        /* Variables CSS pour la cohérence */
        :root {
          --primary-color: #667eea;
          --secondary-color: #764ba2;
          --accent-color: #4f46e5;
          --success-color: #10b981;
          --error-color: #ef4444;
          --text-primary: #1f2937;
          --text-secondary: #6b7280;
          --surface: #ffffff;
          --surface-hover: #f8fafc;
          --border-color: #e5e7eb;
          --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
          --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          --shadow-2xl: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        /* Page principale */
        .smart-login-page {
          background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
          min-height: 100vh;
          position: relative;
          overflow: hidden;
        }

        .smart-login-page::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
          animation: bgMove 20s linear infinite;
        }

        @keyframes bgMove {
          0% { transform: translateX(0) translateY(0); }
          100% { transform: translateX(60px) translateY(60px); }
        }

        /* Éléments flottants */
        .floating-elements {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 1;
        }

        .floating-element {
          position: absolute;
          font-size: 2.5rem;
          opacity: 0.25;
          animation: float 6s ease-in-out infinite;
          filter: drop-shadow(0 4px 8px rgba(0,0,0,0.1));
        }

        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }

        /* Card principale */
        .smart-login-card {
          border-radius: 24px;
          backdrop-filter: blur(15px);
          background: rgba(255, 255, 255, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: var(--shadow-2xl);
          overflow: hidden;
          position: relative;
          z-index: 10;
          animation: slideInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1);
          max-width: 480px;
          margin: 0 auto;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(60px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        /* Header section */
        .login-header-section {
          background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
          padding: 3rem 2rem 2rem;
          position: relative;
          overflow: hidden;
        }

        .login-header-section::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 100%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
          animation: headerGlow 4s ease-in-out infinite;
        }

        @keyframes headerGlow {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.1) rotate(5deg); }
        }

        .login-brand-container {
          text-align: center;
          position: relative;
          z-index: 2;
        }

        .brand-icon-wrapper {
          position: relative;
          display: inline-block;
          margin-bottom: 1.5rem;
        }

        .brand-icon {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.3);
          animation: iconPulse 3s ease-in-out infinite;
          position: relative;
          z-index: 2;
        }

        .brand-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 120px;
          height: 120px;
          background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%);
          border-radius: 50%;
          animation: glowPulse 3s ease-in-out infinite;
          z-index: 1;
        }

        @keyframes iconPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        @keyframes glowPulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
          50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.8; }
        }

        .brand-title {
          color: white;
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
          letter-spacing: -1px;
          text-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .brand-subtitle {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1rem;
          font-weight: 400;
          margin: 0;
        }

        /* Section contenu */
        .login-content-section {
          padding: 2.5rem 2rem 2rem;
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(10px);
        }

        .welcome-message {
          text-align: center;
          margin-bottom: 2rem;
        }

        .welcome-message h3 {
          color: var(--text-primary);
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }

        .welcome-message p {
          color: var(--text-secondary);
          font-size: 0.95rem;
          margin: 0;
        }

        /* Alert personnalisée */
        .custom-alert {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 1rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          position: relative;
          animation: alertSlideIn 0.5s ease;
        }

        .error-alert {
          background: linear-gradient(135deg, rgba(254, 242, 242, 0.9) 0%, rgba(254, 226, 226, 0.9) 100%);
          border: 1px solid rgba(254, 202, 202, 0.8);
          backdrop-filter: blur(5px);
        }

        .alert-icon {
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .alert-content {
          flex: 1;
        }

        .alert-content strong {
          color: var(--error-color);
          font-size: 0.9rem;
          display: block;
          margin-bottom: 0.25rem;
        }

        .alert-content p {
          color: #b91c1c;
          font-size: 0.85rem;
          margin: 0;
        }

        .alert-close {
          background: none;
          border: none;
          color: #b91c1c;
          font-size: 1.25rem;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background-color 0.2s;
        }

        .alert-close:hover {
          background: rgba(185, 28, 28, 0.1);
        }

        @keyframes alertSlideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Formulaire moderne */
        .smart-form {
          margin-bottom: 2rem;
        }

        .form-group-modern {
          margin-bottom: 1.5rem;
        }

        .input-wrapper {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-secondary);
          z-index: 2;
          transition: color 0.3s ease;
        }

        .modern-input {
          width: 100%;
          height: 56px;
          padding: 1rem 1rem 1rem 3rem;
          border: 2px solid rgba(229, 231, 235, 0.6);
          border-radius: 16px;
          font-size: 1rem;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(5px);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          z-index: 1;
        }

        .modern-input:focus {
          outline: none;
          border-color: var(--accent-color);
          box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.1);
          transform: translateY(-2px);
        }

        .modern-input:focus + .floating-label,
        .modern-input:not(:placeholder-shown) + .floating-label {
          transform: translateY(-140%) scale(0.85);
          color: var(--accent-color);
          background: rgba(255, 255, 255, 0.9);
          padding: 0 0.5rem;
        }

        .modern-input:focus ~ .input-icon {
          color: var(--accent-color);
        }

        .floating-label {
          position: absolute;
          left: 3rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-secondary);
          font-size: 1rem;
          font-weight: 500;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          pointer-events: none;
          z-index: 2;
        }

        .password-toggle {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 8px;
          transition: all 0.2s ease;
          z-index: 3;
        }

        .password-toggle:hover {
          color: var(--accent-color);
          background: rgba(79, 70, 229, 0.1);
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.5rem;
          color: var(--error-color);
          font-size: 0.85rem;
          font-weight: 500;
          animation: errorShake 0.5s ease;
        }

        .error-icon {
          font-size: 0.9rem;
        }

        @keyframes errorShake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-3px); }
          75% { transform: translateX(3px); }
        }

        .input-hint {
          margin-top: 0.5rem;
          font-size: 0.8rem;
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        /* Bouton de connexion */
        .smart-login-btn {
          width: 100%;
          height: 56px;
          border: none;
          border-radius: 16px;
          background: linear-gradient(135deg, var(--accent-color) 0%, var(--primary-color) 100%);
          color: white;
          font-size: 1rem;
          font-weight: 600;
          position: relative;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 15px rgba(79, 70, 229, 0.3);
        }

        .smart-login-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(79, 70, 229, 0.4);
        }

        .smart-login-btn:active:not(:disabled) {
          transform: translateY(-1px);
        }

        .smart-login-btn:disabled {
          opacity: 0.7;
          transform: none;
          cursor: not-allowed;
        }

        .btn-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          position: relative;
          z-index: 2;
        }

        .btn-spinner {
          width: 18px;
          height: 18px;
        }

        .btn-arrow {
          transition: transform 0.3s ease;
          font-weight: bold;
        }

        .smart-login-btn:hover .btn-arrow {
          transform: translateX(4px);
        }

        .btn-glow {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          transition: left 0.5s ease;
        }

        .smart-login-btn:hover .btn-glow {
          left: 100%;
        }

        /* Extras et footer */
        .login-extras {
          text-align: center;
          margin-bottom: 2rem;
        }

        .forgot-password-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: var(--accent-color);
          text-decoration: none;
          font-weight: 500;
          font-size: 0.9rem;
          padding: 0.5rem 1rem;
          border-radius: 12px;
          transition: all 0.3s ease;
        }

        .forgot-password-link:hover {
          color: var(--primary-color);
          background: rgba(79, 70, 229, 0.05);
          transform: translateY(-1px);
        }

        .link-icon {
          font-size: 1rem;
        }

        .login-footer {
          text-align: center;
        }

        .footer-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--border-color), transparent);
          margin-bottom: 1.5rem;
        }

        .footer-text {
          color: var(--text-secondary);
          font-size: 0.85rem;
          margin: 0;
          line-height: 1.6;
        }

        .brand-name {
          color: var(--accent-color);
          font-weight: 600;
        }

        /* États de validation */
        .modern-input.is-invalid {
          border-color: var(--error-color);
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
        }

        .modern-input.is-invalid:focus {
          border-color: var(--error-color);
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.2);
        }

        /* Responsive design */
        @media (max-width: 576px) {
          .login-header-section {
            padding: 2rem 1.5rem 1.5rem;
          }
          
          .login-content-section {
            padding: 2rem 1.5rem 1.5rem;
          }
          
          .brand-title {
            font-size: 2rem;
          }
          
          .brand-icon {
            width: 60px;
            height: 60px;
          }
          
          .modern-input {
            height: 52px;
          }
          
          .smart-login-btn {
            height: 52px;
          }
          
          .smart-login-card {
            margin: 1rem;
            border-radius: 20px;
          }
        }

        /* Amélioration de l'accessibilité */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* Mode sombre (optionnel) */
        @media (prefers-color-scheme: dark) {
          .smart-login-card {
            background: rgba(17, 24, 39, 0.95);
          }
          
          .login-content-section {
            color: #f9fafb;
          }
          
          .modern-input {
            background: rgba(31, 41, 55, 0.8);
            border-color: #374151;
            color: #f9fafb;
          }
          
          .floating-label {
            color: #9ca3af;
          }
        }
      `}</style>
    </div>
  );
};

export default LoginForm;